import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import moment from 'moment';
import { Collapse } from 'reactstrap';
import { toast } from 'react-toastify';
import trackImg from "../../assets/img/delivery.svg";
import { getOrder } from '../../services/task.service';
import { TASK_TYPE, TASKS_STATUS, WAREHOUSE_STATUS } from '../../comman/constants';
import PageTitle from '../../comman/page-title';
import useSaasAccount from '../../hooks/useSaasAccount';

const TrackingComponent = () => {
  const { saasAccountId, isOwner, brandingFailed, canUseTracking } = useSaasAccount()
  const [showTrackOrder, setShowTrackOrder] = useOutletContext();

  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [agentDetailsCollapsed, setAgentDetailsCollapsed] = useState(true);
  const [myDetailsCollapsed, setMyDetailsCollapsed] = useState(true);

  const toggleAgentDetails = (e) => {
    e.preventDefault();
    setAgentDetailsCollapsed(!agentDetailsCollapsed);
  }

  const toggleMyDetails = (e) => {
    e.preventDefault();
    setMyDetailsCollapsed(!myDetailsCollapsed);
  }

  const isValidOrderId = (orderId) => {
    const lastHyphenIndex = orderId.lastIndexOf('-');
    if (lastHyphenIndex === -1) {
      return false; // No hyphen found
    }
    const suffix = orderId.slice(lastHyphenIndex + 1);
    return suffix.length >= 3;
  }

  const handleSearch = () => {
    if (!canUseTracking) return

    if (inputValue) {
      if (!isValidOrderId(inputValue)) {
        toast.error('Please enter a valid Order ID.')
        setInputValue('')
        return
      }
      navigate(`/${inputValue.trim()}`)
    }
  }

  useEffect(() => {
    setInputValue('');
  }, [showTrackOrder, setInputValue])

  useEffect(() => {
    setIsVisible(true);
    if (orderId) {
      if (!canUseTracking) return
      fetchOrder()
    } else {
      setShowTrackOrder(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, canUseTracking, saasAccountId])

  const fetchOrder = async () => {
    setIsLoading(true)
    try {
      const { data } = await getOrder(orderId, isOwner ? null : saasAccountId);
      setOrder(data.orderData)
      setShowTrackOrder(false)
    } catch (error) {
      toast.dismiss();
      toast.error(error?.data?.message || error?.message);
      if (inputValue) setInputValue('');
      navigate('/')
    }
    setIsLoading(false)
  }

  const isReceived = useMemo(() => {
    return order?.firstTransaction?.createdAt
      &&
      (order?.firstTransaction?.warehouse_status_id === WAREHOUSE_STATUS.RECEIVED);
  }, [order])

  const isOutForDelivery = useMemo(() => {
    return order?.latestTransaction?.createdAt
      &&
      (order?.latestTransaction?.warehouse_status_id === WAREHOUSE_STATUS.DISPATCHED);
  }, [order])

  const isCancelledOrder = useMemo(() => {
    return order?.task_status === TASKS_STATUS.CANCEL
  }, [order])

  const finalStatusText = useMemo(() => {
    if (order?.is_warehouse) {
      if (order?.job_status === 'Rescheduled') {
        return 'Rescheduled';
      } else if (order?.task_status === TASKS_STATUS.CANCEL) {
        return 'Canceled';
      } else if (order?.task_status === TASKS_STATUS.FAILED) {
        return 'Failed';
      } else {
        return 'Successful';
      }
    } else {
      if (order?.task_status === TASKS_STATUS.CANCEL) {
        return 'Canceled';
      } else if (order?.task_status === TASKS_STATUS.FAILED) {
        return 'Failed';
      } else {
        return 'Successful';
      }
    }
  }, [order])

  const finalStatusClass = useMemo(() => {
    if(order?.job_type !== TASK_TYPE.DELIVERY) {
      return '';
    }

    if (order?.is_warehouse) {
      if (
        (order?.job_status === 'Rescheduled')
        ||
        (order?.task_status === TASKS_STATUS.CANCEL)
        ||
        (order?.task_status === TASKS_STATUS.FAILED)
        ||
        (order?.task_status === TASKS_STATUS.SUCCESSFUL)
      ) {
        return 'completed';
      }
    } else {
      if (
        (order?.task_status === TASKS_STATUS.CANCEL)
        ||
        (order?.task_status === TASKS_STATUS.FAILED)
        ||
        (order?.task_status === TASKS_STATUS.SUCCESSFUL)
      ) {
        return 'completed';
      }
    }
    return '';
  }, [order])

  return (
    <>
      <PageTitle title="Track Order" description="Track your delivery order with YFS" />    
      <main className="bg_gray">
        <div className={`container margin_60_20 ${isVisible ? 'fadeIn' : ''}`}>
          {brandingFailed ? (
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="alert alert-danger" role="alert">
                  Unable to load account configuration. Please refresh.
                </div>
              </div>
            </div>
          ) : null}
          <>
            {
              showTrackOrder
                ?
                <div className="row justify-content-center">
                  <div className="col-lg-8">
                    <div className="box_general write_review">
                      <div className="row justify-content-center">
                        <figure>
                          <img src={trackImg} alt="YFS" width="150" height="165" />
                        </figure>
                      </div>
                      <div className="main_title center mb-2">
                        <span><em></em></span>
                      </div>
                      <h4 className='mt-3 mb-3 text-center'>Track Your Order</h4>
                      <div className="row no-gutters custom-search-input">
                        <div className="col-lg-10">
                          <div className="form-group">
                            <input className="form-control no_border_r" type="text" placeholder="Want to track your order?"
                              maxLength={50}
                              value={inputValue}
                              disabled={!canUseTracking}
                              onChange={(e) => setInputValue(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-lg-2">
                          <button className="btn_1 gradient" type="button" onClick={handleSearch} disabled={isLoading || !canUseTracking}>Search</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                :
                <div className="row justify-content-center">
                  <div className="col-lg-8">
                    <div className="box_general write_review">
                      <div className="main_title center mb-2">
                        <span><em></em></span>
                      </div>
                      <div className="d-flex mb-3" style={{ flexDirection: 'column' }}>
                        <p className="mb-0">Hello, <span className="strong customer-name">{order?.customer_name}</span></p>
                        <p className="mb-0">Order Number: <span className="strong">{order?.order_id}</span></p>
                        <div className="d-flex justify-content-between">
                          <p className="mb-0">Status: <span className="strong">{order?.job_status}</span></p>
                          {
                            (order?.is_warehouse && !order?.completed_datetime)
                              ?
                              <p>ETA: <span className="strong">
                                {
                                  isReceived
                                    ? moment.utc(order.firstTransaction.createdAt).add(3, 'days').local().format('D MMM YY')
                                    : ''
                                }
                              </span>
                              </p>
                              : ''
                          }
                        </div>
                        {
                          (isCancelledOrder && order?.cancel_reason)
                            ?
                            <p className="mb-0">Cancel Reason: <span className="strong">{order.cancel_reason}</span></p>
                            : ''
                        }
                      </div>
                      {
                        order?.is_warehouse
                          ?
                          <>
                            <div className="row justify-content-between">
                              <div className={`order-tracking ${isReceived ? 'completed' : ''}`} style={{ width: '33%' }}>
                                <span className="is-complete"></span>
                                <p>Received<br />
                                  <span>
                                    {
                                      isReceived
                                        ? moment.utc(order.firstTransaction.createdAt).local().format('D MMM YY hh:mm A')
                                        : ''
                                    }
                                  </span>
                                </p>
                              </div>
                              <div className={`order-tracking ${isOutForDelivery ? 'completed' : ''}`} style={{ width: '34%' }}>
                                <span className="is-complete"></span>
                                <p>Out For Delivery<br />
                                  <span>
                                    {
                                      isOutForDelivery
                                        ? moment.utc(order.latestTransaction.createdAt).local().format('D MMM YY hh:mm A')
                                        : ''
                                    }
                                  </span>
                                </p>
                              </div>
                              <div className={`order-tracking ${order?.completed_datetime ? 'completed' : ''}`} style={{ width: '33%' }}>
                                <span className="is-complete"></span>
                                <p>{finalStatusText}<br />
                                  <span>
                                    {
                                      order?.completed_datetime
                                        ? moment.utc(order.completed_datetime).local().format('D MMM YY hh:mm A')
                                        : ''
                                    }
                                  </span>
                                </p>
                              </div>
                            </div>
                          </>
                          :
                          <>
                            <div className="row justify-content-between">
                              <div className={`order-tracking ${order?.order_creation_datetime ? 'completed' : ''}`} style={{ width: '25%' }}>
                                <span className="is-complete"></span>
                                <p>Created<br />
                                  <span>
                                    {
                                      order?.order_creation_datetime
                                        ? moment.utc(order.order_creation_datetime).local().format('D MMM YY hh:mm A')
                                        : ''
                                    }
                                  </span>
                                </p>
                              </div>
                              <div className={`order-tracking ${order?.assigned_datetime ? 'completed' : ''}`} style={{ width: '25%' }}>
                                <span className="is-complete"></span>
                                <p>Assigned<br />
                                  <span>
                                    {
                                      order?.assigned_datetime
                                        ? moment.utc(order.assigned_datetime).local().format('D MMM YY hh:mm A')
                                        : ''
                                    }
                                  </span>
                                </p>
                              </div>
                              <div className={`order-tracking ${order?.started_datetime ? 'completed' : ''}`} style={{ width: '25%' }}>
                                <span className="is-complete"></span>
                                <p>Out For Delivery<br />
                                  <span>
                                    {
                                      order?.started_datetime
                                        ? moment.utc(order.started_datetime).local().format('D MMM YY hh:mm A')
                                        : ''
                                    }
                                  </span>
                                </p>
                              </div>
                              <div className={`order-tracking ${finalStatusClass}`} style={{ width: '25%' }}>
                                <span className="is-complete"></span>
                                <p>{finalStatusText}<br />
                                  <span>
                                    {
                                      order?.completed_datetime
                                        ? moment.utc(order.completed_datetime).local().format('D MMM YY hh:mm A')
                                        : ''
                                    }
                                  </span>
                                </p>
                              </div>
                            </div>
                          </>
                      }
                      <div className="row mt-5">
                        <aside className="col-lg-12" id="sidebar_fixed">
                          <div className="">
                            <div className="filter_type">
                              <h4><a href="#agent-details" onClick={toggleAgentDetails} className={`arrow ${agentDetailsCollapsed ? "closed" : "opened"}`} aria-expanded={!agentDetailsCollapsed}>Agent Details</a></h4>
                              <Collapse isOpen={!agentDetailsCollapsed} id="agent-details">
                                <div className="list_articles clearfix">
                                  <ul>
                                    <li>
                                      <span><i className="icon_profile"></i><strong className='mr-1'>Agent Name: </strong>{order?.fleet_name}</span>
                                    </li>
                                    <li>
                                      <span><i className="icon_phone"></i><strong className='mr-1'>Phone Number: </strong>{order?.fleet_phone}</span>
                                    </li>
                                  </ul>
                                </div>
                              </Collapse>
                            </div>
                            <div className="filter_type">
                              <h4><a href="#my-details" onClick={toggleMyDetails} className={`arrow ${myDetailsCollapsed ? "closed" : "opened"}`} aria-expanded={!myDetailsCollapsed}>My Details</a></h4>
                              <Collapse isOpen={!myDetailsCollapsed} id="my-details">
                                <div className="list_articles clearfix">
                                  <ul>
                                    <li>
                                      <span><i className="icon_profile"></i><strong className='mr-1'>Customer Name: </strong>{order?.customer_name}</span>
                                    </li>
                                    <li>
                                      <span><i className="icon_phone"></i><strong className='mr-1'>Phone Number: </strong>{order?.customer_phone}</span>
                                    </li>
                                    <li>
                                      <span><i className="icon_pin"></i><strong className='mr-1'>Address: </strong>{order?.job_address}</span>
                                    </li>
                                    <li>
                                      <span><i className="icon_building_alt"></i><strong className='mr-1'>Company Name: </strong>{order?.company_name}</span>
                                    </li>
                                    <li>
                                      <span><i className="icon_currency_alt"></i><strong className='mr-1'>Order COD: </strong>{order?.actual_cod ? `${order?.actual_cod} EGP` : ''}</span>
                                    </li>
                                    <li id='comment'>
                                      <div className="promo">
                                        {/* <h3>Free Delivery for your first 14 days!</h3> */}
                                        <p>If you encounter any incorrect information in the "My Details" section, please don't hesitate to reach out through WHATSAPP to us for assistance.</p>
                                        <i className="icon-food_icon_delivery"></i>
                                      </div>
                                    </li>
                                  </ul>
                                </div>
                              </Collapse>
                            </div>
                          </div>
                        </aside>
                      </div>
                    </div>
                  </div>
                </div>
            }
          </>
        </div>
      </main>
    </>
  )
}

export default TrackingComponent;